import type { GamesFilters } from '@/queries/games/games.schema';
import type { QuestFilter } from '@/queries/userQuest/userQuest.schema';

type MissionGamesFilters = NonNullable<GamesFilters>;
type MissionGamesFilterField
  = | 'details.category'
    | 'details.volatility'
    | 'details.rtpType'
    | 'details.features'
    | 'details.gameMechanics'
    | 'details.themes'
    | 'provider.alias';
type MissionGamesFilterValue = NonNullable<MissionGamesFilters[MissionGamesFilterField]>;

type MissionGamesFilterMapping = {
  field: MissionGamesFilterField;
  use: 'label' | 'value';
};

const MISSION_GAMES_FILTER_BY_TYPE: Record<string, MissionGamesFilterMapping> = {
  category: { field: 'details.category', use: 'label' },
  volatility: { field: 'details.volatility', use: 'label' },
  rtpType: { field: 'details.rtpType', use: 'label' },
  feature: { field: 'details.features', use: 'label' },
  mechanic: { field: 'details.gameMechanics', use: 'label' },
  theme: { field: 'details.themes', use: 'label' },
  provider: { field: 'provider.alias', use: 'value' },
};

const MISSION_GAMES_FILTER_FIELDS = new Set<MissionGamesFilterField>(
  Object.values(MISSION_GAMES_FILTER_BY_TYPE).map(({ field }) => field),
);
const MISSION_GAMES_FILTER_QUERY_KEY_PATTERN = /^filters\[([^\]]+)\](?:\[\$in\](?:\[\])?)?$/;

const appendFilterValue = (
  filters: MissionGamesFilters,
  field: MissionGamesFilterField,
  value: string,
) => {
  const currentValue = filters[field];

  if (!currentValue) {
    filters[field] = value as MissionGamesFilterValue;
    return;
  }

  if (typeof currentValue === 'string') {
    filters[field] = { $in: [currentValue, value] } as MissionGamesFilterValue;
    return;
  }

  filters[field] = {
    $in: [...(currentValue.$in ?? []), value],
  } as MissionGamesFilterValue;
};

export const buildMissionGamesFilters = (questFilters?: QuestFilter[] | null): GamesFilters => {
  if (!questFilters?.length) {
    return undefined;
  }

  const gamesFilters: MissionGamesFilters = {};

  questFilters.forEach((filter) => {
    const mapping = MISSION_GAMES_FILTER_BY_TYPE[filter.type];

    if (!mapping) {
      return;
    }

    appendFilterValue(gamesFilters, mapping.field, filter[mapping.use]);
  });

  return Object.keys(gamesFilters).length ? gamesFilters : undefined;
};

export const hasMissionGamesFilters = (questFilters?: QuestFilter[] | null) =>
  !!buildMissionGamesFilters(questFilters);

export const buildMissionGamesSearchParams = (filters: GamesFilters, page = 1) => {
  const params = new URLSearchParams();

  if (page > 1) {
    params.set('page', String(page));
  }

  if (!filters) {
    return params;
  }

  Object.entries(filters).forEach(([field, filterValue]) => {
    if (!MISSION_GAMES_FILTER_FIELDS.has(field as MissionGamesFilterField) || !filterValue) {
      return;
    }

    if (typeof filterValue === 'string') {
      params.append(`filters[${field}]`, filterValue);
      return;
    }

    if (typeof filterValue === 'object' && '$in' in filterValue) {
      filterValue.$in?.forEach(value => params.append(`filters[${field}][$in][]`, value));
    }
  });

  return params;
};

export const parseMissionGamesFiltersFromSearchParams = (
  searchParams: Pick<URLSearchParams, 'forEach'>,
): GamesFilters => {
  const filters: MissionGamesFilters = {};

  searchParams.forEach((value, key) => {
    const field = key.match(MISSION_GAMES_FILTER_QUERY_KEY_PATTERN)?.[1] as MissionGamesFilterField | undefined;

    if (!field || !MISSION_GAMES_FILTER_FIELDS.has(field)) {
      return;
    }

    appendFilterValue(filters, field, value);
  });

  return Object.keys(filters).length ? filters : undefined;
};
